import { Link, useNavigate, useRouter } from '@tanstack/react-router';

import { t } from '@web/i18n';
import { AppIcons, Button, VollMark } from '@web/ui/core/atoms';
import { fireAndForget } from '@web/ui/core/utils';

export function NotFoundPage(): React.JSX.Element {
  const router = useRouter();
  const navigate = useNavigate();

  const handleBack = (): void => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    fireAndForget(navigate({ to: '/' }));
  };

  return (
    <main className="relative grid min-h-screen place-items-center overflow-hidden bg-background px-6 py-16">
      <Backdrop />

      <div className="relative z-[1] flex w-full max-w-prose-xl flex-col items-center text-center">
        <div className="mb-8 inline-flex items-center gap-2.5 font-mono text-tiny font-medium uppercase tracking-eyebrow-widest text-faint-foreground">
          <span aria-hidden className="h-1 w-1 rounded-pill bg-primary" />
          {t('notFound.eyebrow')}
          <span aria-hidden className="h-1 w-1 rounded-pill bg-primary" />
        </div>

        <h1
          aria-label="404"
          className="m-0 flex select-none items-center gap-2 font-serif leading-[0.85] tracking-display-extra"
        >
          <Digit>4</Digit>
          <OrbitZero />
          <Digit>4</Digit>
        </h1>

        <div aria-hidden className="mt-10 h-px w-12 bg-border-strong/40" />

        <h2 className="mt-7 m-0 font-serif text-display-lg font-normal leading-tight tracking-tight text-foreground md:text-display-md">
          <em className="text-primary [font-style:italic]">{t('notFound.titleEm')}</em>{' '}
          {t('notFound.titleAfter')}
        </h2>

        <p className="m-0 mt-3 max-w-prose leading-relaxed text-meta text-muted-foreground [text-wrap:pretty]">
          {t('notFound.body')}
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
          <Button variant="outline" onClick={handleBack}>
            <AppIcons name="arrow-left" size={13} />
            {t('notFound.back')}
          </Button>
          <Button variant="primary" asChild>
            <Link to="/">
              {t('notFound.backHome')}
              <AppIcons name="arrow-right" size={13} />
            </Link>
          </Button>
        </div>

        <div className="mt-14 inline-flex items-center gap-2 text-faint-foreground">
          <VollMark height={10} />
          <span className="font-mono text-mono-xs uppercase tracking-eyebrow-widest">
            {t('notFound.signature')}
          </span>
        </div>
      </div>
    </main>
  );
}

function Digit({ children }: Readonly<{ children: string }>): React.JSX.Element {
  return (
    <span className="block text-[clamp(120px,18vw,200px)] text-primary [font-style:italic]">
      {children}
    </span>
  );
}

/**
 * Middle "0" of 404 — a serif-italic glyph behind a thin terracotta ring,
 * so the page reads as 4·0·4 while the centerpiece carries the brand mark.
 */
function OrbitZero(): React.JSX.Element {
  return (
    <span
      aria-hidden
      className="relative inline-flex h-[clamp(120px,18vw,200px)] w-[clamp(86px,13vw,148px)] items-center justify-center"
    >
      <span className="absolute inset-y-decor-ring-outer left-1/2 aspect-square -translate-x-1/2 rounded-pill border border-primary/35" />
      <span className="absolute inset-y-decor-ring-inner left-1/2 aspect-square -translate-x-1/2 rounded-pill border border-primary-tint-strong/60" />
      <span className="relative block text-[clamp(120px,18vw,200px)] text-faint-foreground/30 [font-style:italic]">
        0
      </span>
    </span>
  );
}

/**
 * Decorative axis: two faint vertical hairlines framing the centerpiece,
 * fading at the top/bottom so they read as a printed-page rule rather than
 * a full grid. Pure typography backdrop, no animation.
 */
function Backdrop(): React.JSX.Element {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
      <div className="absolute inset-y-12 left-decor-rail w-px bg-gradient-to-b from-transparent via-border-strong/30 to-transparent" />
      <div className="absolute inset-y-12 right-decor-rail w-px bg-gradient-to-b from-transparent via-border-strong/30 to-transparent" />
    </div>
  );
}
